import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';
import { IconButton } from '@saucelabs/ds-button';
import { InputText } from '@saucelabs/ds-inputs';
import { Tooltip } from '@saucelabs/ds-tooltip';
import {
  records as appStorageRecords,
} from '@saucelabs/sl-module-app-storage';
import { IconEditFill } from '@saucelabs/ds-icons';
import { Typography } from '@saucelabs/ds-typography';

import constants from '../constants';
import operations from '../operations';
import { sanitizeAppStorageFileDescription } from '../utils';

type TableCellDescriptionProps = {
  file: appStorageRecords.AppStorageFile,
};

const TableCellDescription: React.FC<TableCellDescriptionProps> = ({ file }) => {
  const {
    description,
  } = file;
  const dispatch = useDispatch<ReduxDispatcher>();
  const [editedDescription, setEditedDescription] = useState(description);
  const [descriptionBeingEdited, setDescriptionBeingEdited] = useState(false);

  const persistDescription = () => {
    setDescriptionBeingEdited(false);
    if (editedDescription !== null && editedDescription !== description) {
      void dispatch(operations.updateFileDescription(file, editedDescription));
    }
  };

  const persistDescriptionOnEnter = ({ key }) => {
    if (key === 'Enter') {
      void persistDescription();
    }
  };

  const onChangeDescription = (newValue) => {
    const sanitizedDescription = sanitizeAppStorageFileDescription(newValue);
    setEditedDescription(sanitizedDescription);
  };

  return (
    <div>
      { descriptionBeingEdited ? (
        <InputText
          autoFocus
          data-qa="app-management-files-table-cell-description-input"
          excludeFromTabOrder
          label={undefined}
          maxLength={constants.FILE_DESCRIPTION_MAX_LENGTH}
          onBlur={persistDescription}
          onChange={onChangeDescription}
          onKeyDown={persistDescriptionOnEnter}
          placeholder="max. 255 chars, no emoji"
          value={editedDescription ?? undefined}
        />
      ) : (
        <div
          className="flex flex-row items-start"
          data-qa="app-management-files-table-cell-description-label"
        >
          <Typography className={classnames('font-semibold break-all', {
            'text-disabled-emphasis': !file.description,
          })}
          >
            {file.description || '-'}
          </Typography>

          <Tooltip
            placement="top"
            title="Edit Description"
          >
            <IconButton
              className="ml-1 opacity-0 group-hover:opacity-100"
              color="primary"
              data-qa="app-management-files-table-cell-description-edit-button"
              icon={<IconEditFill />}
              onPress={() => setDescriptionBeingEdited(true)}
              size="sm"
              variant="text"
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default TableCellDescription;
